// Person/crew/musician deletion — the ONE place that builds the warning text.
//
// Why (measured 2026-10-15): `server/routes/team.js:988` — `DELETE /api/team/:id`
// destroys the person's `team_members` row AND every `team_member_bands` row
// (line 1004), so a plain "Are you sure?" silently dropped somebody out of ALL
// their bands. A real incident: the user only wanted to unlink a person from one
// band and lost the card + 2 links (restored by hand from a prod safepoint).
// The dialog must therefore NAME the consequences from the REAL data.
//
// What `DELETE /api/team/:id` does NOT destroy (verified 2026-10-15): the band's
// rider cards (`rider_musicians` / `rider_crew` rider JSON). Saying so is part of
// the contract, so the user is not scared into doing nothing.
//
// Reuses the shipped `showConfirm` bus (ConfirmDialog renders multi-line text);
// no new dialog component.

import { showConfirm } from '../components/ConfirmDialog';

/** A band link as `apiGetTeamMember` / `apiGetTeam` return it in `member.bands`. */
export interface PersonBandLink {
  id: string;
  name?: string;
  slug?: string;
  link_role?: string;
  roles?: string[];
}

/** The subset of DTOs this helper needs — Team member, rider musician or crew card. */
export interface DeletablePerson {
  id: string;
  name?: string;
  // Team member
  bands?: PersonBandLink[];
  passports?: unknown[];
  visas?: unknown[];
  airlines?: unknown[];
  loyalty_programs?: unknown[];
  // Rider card
  fonction?: string;
  role?: string;
}

export interface DeletePersonnelCopy {
  title: string;
  /** Paragraph blocks, joined with a blank line in the modal. */
  lines: string[];
  /** Checkbox label — ONLY when the person sits in more than one band and is known. */
  ackLabel?: string;
}

const dash = '—';

const count = (v: unknown[] | undefined) => (Array.isArray(v) ? v.length : 0);

/** Human list of a person's band names. `undefined` = we do NOT know the links. */
export function bandNames(m: DeletablePerson): string[] | undefined {
  if (!Array.isArray(m.bands)) return undefined;
  return m.bands.map((b) => b.name || b.slug || b.id);
}

/**
 * Build the deletion warning for a PERSON (a `team_members` row). `t` is the
 * i18next translator of the active language, typed structurally so the helper
 * needs no i18next import (and stays trivially testable).
 */
export function buildPersonDeleteCopy(
  m: DeletablePerson,
  t: (key: string, opts?: Record<string, unknown>) => string,
): DeletePersonnelCopy {
  const name = m.name || t('team.deletionUnnamed');
  const names = bandNames(m);
  const lines: string[] = [t('team.deletePersonIntro', { name })];

  if (names === undefined) {
    // We refuse to invent a band list: say we do not know (never print "none").
    lines.push(t('team.deleteConfirmBandsUnknown'));
  } else if (names.length > 0) {
    lines.push(t('team.deleteConfirmBandsIntro', {
      count: names.length,
      names: names.join(', '),
    }));
  }

  const docs: string[] = [];
  const p = count(m.passports);
  const v = count(m.visas);
  const a = count(m.airlines) + count(m.loyalty_programs);
  if (p > 0) docs.push(t('team.deleteDocPassports', { count: p }));
  if (v > 0) docs.push(t('team.deleteDocVisas', { count: v }));
  if (a > 0) docs.push(t('team.deleteDocLoyalty', { count: a }));
  if (docs.length > 0) lines.push(t('team.deleteConfirmDocsIntro', { items: docs.join(', ') }));

  lines.push(t('team.deleteRiderKept'));
  lines.push(t('team.deleteAltPath'));
  if (names === undefined || names.length > 0) lines.push(t('team.deleteNoUndo'));

  return {
    title: t('team.deleteTitle', { name }),
    lines,
    ackLabel: names !== undefined && names.length > 1
      ? t('team.deleteUnderstand', { name, count: names.length })
      : undefined,
  };
}

/**
 * Build the deletion warning for a RIDER CARD (musician/crew in ONE band). This
 * one does NOT touch the Team registry — say it, so nobody believes they are
 * deleting the person.
 */
export function buildCardDeleteCopy(
  m: DeletablePerson,
  kind: 'musician' | 'crew',
  t: (key: string, opts?: Record<string, unknown>) => string,
): DeletePersonnelCopy {
  const name = m.name || t('team.deletionUnnamed');
  const role = m.fonction || m.role || dash;
  return {
    title: t(`${kind}.deleteTitle`),
    lines: [
      t(`${kind}.deleteCardBody`, { name, role }),
      t(`${kind}.deleteCardKeptPerson`),
    ],
  };
}

/**
 * Ask for confirmation. Multi-band → the dialog renders an "I understand"
 * checkbox and resolves `true` only when it is ticked (enforced by the provider).
 * Returns false on cancel, Escape, backdrop click, and on an incomplete
 * reinforced confirmation.
 */
export function confirmPersonnelDelete(copy: DeletePersonnelCopy): Promise<boolean> {
  return showConfirm(copy.lines.join('\n\n'), {
    title: copy.title,
    danger: true,
    ack: copy.ackLabel,
  } as never);
}
